import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { ErrorMessage, Field, Form, Formik } from "formik";
import EditIcon from "../../../../assets/icons/edit.svg";
import TrashIcon from "../../../../assets/icons/ic_delete.svg";
import { AlertMsg } from "../../../../shared/components/alert-msg";
import { LoadingItem } from "../../../../shared/components/loading";
import { useToast } from "../../../../shared/context/ToastContext";
import { allowNumbersOnly, numberWithCommas } from "../../../../shared/utils";
import {
  EditableProductAttribute,
  ProductAttributeTemplate,
  ProductAttributeTemplateAttribute,
  Variant,
} from "../../../../types/ProductTypes";
import {
  buildVariantDisplayName,
  getVariantDisplayName,
  getVariantSummaryEntries,
} from "../../../../modules/inapp/products/utils/productPayload";
import { ImageDrawer } from "./ImageDrawer";
import "./ProductVarianceModal.scss";

interface ProductVarianceModalProps {
  show: boolean;
  onClose: () => void;
  attributes: EditableProductAttribute[];
  setAttributes: (attributes: EditableProductAttribute[]) => void;
  variants: Variant[];
  setVariants: (variant: Variant[]) => void;
  pageProps: any;
  sizeOfFetch: number;
  editIndex?: number | null;
  initialEditVariant?: Variant | null;
  productName: string;
}

type ModalView = "templates" | "variants";
type SubmitMode = "close" | "continue";

type VariantFormValues = {
  variantName: string;
  attributeSelections: Record<string, string>;
  price: string;
  stockQuantity: string;
  addDiscount: boolean;
  discountPercentage: string;
  discountPrice: string;
  discountStartDate: Date | null;
  discountEndDate: Date | null;
  images: Array<{ url: string; otherNames?: string[] }>;
};

const normalizeText = (value: string) => value.replace(/\s+/g, " ").trim();

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const toVariantNameText = (value: string) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const toAttributeCode = (value: string) =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

const isActiveItem = (item: { isActive?: boolean }) => item.isActive !== false;

const sortBySortOrder = <T extends { sortOrder?: number }>(items: T[]) =>
  [...items].sort(
    (left, right) => (left.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right.sortOrder ?? Number.MAX_SAFE_INTEGER),
  );

const parseNumericValue = (value: string) => {
  const normalizedValue = String(value ?? "").replace(/,/g, "").trim();

  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatDateForPayload = (value: Date | null) => (value ? value.toISOString() : undefined);

const isPersistedEntityId = (value: unknown) => {
  if (typeof value !== "string") {
    return false;
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    normalizedValue,
  );
};

const getTemplateAttributes = (template: ProductAttributeTemplate) =>
  sortBySortOrder(template.attributes ?? [])
    .filter(isActiveItem)
    .map((attribute) => attribute.name)
    .filter(Boolean);

const toEditableAttributes = (
  templateAttributes: ProductAttributeTemplateAttribute[] = [],
  templateMeta?: Pick<ProductAttributeTemplate, "id" | "name"> | null,
): EditableProductAttribute[] =>
  sortBySortOrder(
    templateAttributes.filter(isActiveItem).map((attribute, index) => ({
      name: attribute.name,
      code: attribute.code || toAttributeCode(attribute.name),
      type: "select" as const,
      isRequired: attribute.isRequired ?? true,
      sortOrder: attribute.sortOrder ?? index + 1,
      mode: "text" as const,
      templateId: templateMeta?.id,
      templateName: templateMeta?.name,
      options: sortBySortOrder(
        (attribute.options ?? [])
          .filter((option) => option?.isActive !== false)
          .map((option, optionIndex) => ({
            id: option.id,
            value: option.value || option.name || option.label || "",
            label: option.label || option.name || option.value || "",
            sortOrder: option.sortOrder ?? optionIndex + 1,
            imageUrl: option.imageUrl ?? null,
            priceModifier: option.priceModifier ?? null,
          }))
          .filter((option) => option.value && option.label),
      ),
    })),
  );

const getTemplateMetaFromAttributes = (items: EditableProductAttribute[]) => {
  const firstAttribute = items[0];

  if (!firstAttribute) {
    return null;
  }

  return {
    id: firstAttribute.templateId,
    name: firstAttribute.templateName || "Selected attribute template",
  };
};

const buildAttributeSelections = (
  variant?: Variant | null,
  availableAttributes: EditableProductAttribute[] = [],
) =>
  (variant?.attributeValues ?? []).reduce<Record<string, string>>((accumulator, attributeValue) => {
    const attributeName = String(attributeValue.attributeName ?? "");
    const matchingAttribute = availableAttributes.find((attribute) => {
      const attributeCode = String(attribute.code ?? "");
      const normalizedAttributeName = normalizeText(attribute.name).toLowerCase();
      const normalizedVariantName = normalizeText(attributeName).toLowerCase();

      return (
        attributeCode === attributeName ||
        attributeCode === toAttributeCode(attributeName) ||
        normalizedAttributeName === normalizedVariantName
      );
    });

    const nextKey = matchingAttribute?.code || toAttributeCode(attributeName);
    accumulator[nextKey] = String(attributeValue.value ?? "");
    return accumulator;
  }, {});

const buildVariantCombinationKey = (attributeValues: Variant["attributeValues"]) =>
  [...attributeValues]
    .map((attributeValue) => ({
      key: toAttributeCode(attributeValue.attributeName),
      value: String(attributeValue.value ?? "").trim().toLowerCase(),
    }))
    .sort((left, right) => `${left.key}:${left.value}`.localeCompare(`${right.key}:${right.value}`))
    .map((item) => `${item.key}:${item.value}`)
    .join("|");

const hasDuplicateVariantCombination = (items: Variant[]) => {
  const seenKeys = new Set<string>();

  for (const variant of items) {
    const combinationKey = buildVariantCombinationKey(variant.attributeValues ?? []);

    if (!combinationKey) {
      continue;
    }

    if (seenKeys.has(combinationKey)) {
      return true;
    }

    seenKeys.add(combinationKey);
  }

  return false;
};

const buildVariantNameFromSelections = (
  attributeSelections: Record<string, string>,
  availableAttributes: EditableProductAttribute[] = [],
) =>
  availableAttributes
    .map((attribute) => {
      const selectedValue = attributeSelections[attribute.code];

      if (!selectedValue) {
        return "";
      }

      const matchingOption = attribute.options.find((option) => option.value === selectedValue);
      return matchingOption?.label || selectedValue;
    })
    .filter(Boolean)
    .join(" / ");

const buildVariantNameFromProductSelections = (
  productName: string,
  attributeSelections: Record<string, string>,
  availableAttributes: EditableProductAttribute[] = [],
  fallbackName?: string,
  fallbackIndex?: number,
) =>
  buildVariantDisplayName(
    productName,
    buildVariantNameFromSelections(attributeSelections, availableAttributes),
    fallbackName,
    fallbackIndex,
  );

const getUniqueBadgeEntries = (
  entries: Array<{ key: string; value: string }> = [],
) => {
  const seenEntries = new Set<string>();

  return entries.filter((entry) => {
    const normalizedKey = normalizeText(String(entry?.key ?? ""));
    const normalizedValue = normalizeText(String(entry?.value ?? ""));
    const entryKey = `${normalizedKey.toLowerCase()}::${normalizedValue.toLowerCase()}`;

    if (!normalizedKey || !normalizedValue || seenEntries.has(entryKey)) {
      return false;
    }

    seenEntries.add(entryKey);
    return true;
  });
};

const getStoredVariantDisplayName = (
  variant: Variant,
  index: number,
  availableAttributes: EditableProductAttribute[] = [],
  productName = "",
) => {
  const savedVariantName = toVariantNameText(String(variant?.name ?? "").trim());

  if (savedVariantName) {
    return savedVariantName;
  }

  return getVariantDisplayName(variant, index, availableAttributes, productName);
};

const buildVariantFormInitialValues = (
  variant?: Variant | null,
  availableAttributes: EditableProductAttribute[] = [],
): VariantFormValues => {
  const attributeSelections = buildAttributeSelections(variant, availableAttributes);
  const generatedVariantName = buildVariantNameFromSelections(
    attributeSelections,
    availableAttributes,
  );

  return {
    variantName: toVariantNameText(variant?.name || generatedVariantName || ""),
    attributeSelections,
    price:
      variant?.price?.amount !== undefined && variant?.price?.amount !== null
        ? String(variant.price.amount)
        : "",
    stockQuantity:
      variant?.stockQuantity !== undefined && variant?.stockQuantity !== null
        ? String(variant.stockQuantity)
        : "",
    addDiscount: Boolean(variant?.discountPrice?.amount),
    discountPercentage:
      variant?.discountPercentage !== undefined && variant?.discountPercentage !== null
        ? String(variant.discountPercentage)
        : "",
    discountPrice:
      variant?.discountPrice?.amount !== undefined && variant?.discountPrice?.amount !== null
        ? String(variant.discountPrice.amount)
        : "",
    discountStartDate: variant?.discountPrice?.startDate
      ? new Date(variant.discountPrice.startDate)
      : null,
    discountEndDate: variant?.discountPrice?.dueDate
      ? new Date(variant.discountPrice.dueDate)
      : null,
    images: Array.isArray(variant?.images)
      ? (variant?.images ?? [])
          .filter((image: any) => image?.url)
          .slice(0, 3)
          .map((image: any) => ({
            url: image.url,
            otherNames: image.otherNames ?? [],
          }))
      : Array.isArray(variant?.urls)
        ? (variant?.urls ?? []).slice(0, 3).map((url: string) => ({ url }))
        : [],
  };
};

const ProductVarianceModal = ({
  show,
  onClose,
  attributes,
  setAttributes,
  variants,
  setVariants,
  pageProps,
  editIndex,
  initialEditVariant,
  productName,
}: ProductVarianceModalProps) => {
  const { showToast } = useToast();
  const hasExistingAttributes = attributes.length > 0;
  const getAttributeTemplatesAction = pageProps?.getAttributeTemplatesAction;
  const getAttributeTemplateDetailAction = pageProps?.getAttributeTemplateDetailAction;
  const [modalView, setModalView] = useState<ModalView>(
    hasExistingAttributes ? "variants" : "templates",
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplateMeta, setSelectedTemplateMeta] = useState<ProductAttributeTemplate | null>(null);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [variantAttributes, setVariantAttributes] = useState<EditableProductAttribute[]>([]);
  const [openedWithExistingAttributes, setOpenedWithExistingAttributes] = useState(false);
  const [activeEditIndex, setActiveEditIndex] = useState<number | null>(editIndex ?? null);
  const [submitMode, setSubmitMode] = useState<SubmitMode>("close");
  const [showVariantImagesModal, setShowVariantImagesModal] = useState(false);
  const hasInitializedOpenState = useRef(false);
  const hasShownMissingProductNameRef = useRef(false);
  const formikRef = useRef<any>(null);
  const submitModeRef = useRef<SubmitMode>("close");

  const getValidationToastMessage = (errors: any) => {
    const missingItems: string[] = [];

    if (errors?.attributeSelections && typeof errors.attributeSelections === "object") {
      variantAttributes.forEach((attribute) => {
        if (errors.attributeSelections?.[attribute.code]) {
          missingItems.push(attribute.name);
        }
      });
    }

    if (errors?.price) {
      missingItems.push("Price");
    }

    if (errors?.stockQuantity) {
      missingItems.push("Stock quantity");
    }

    if (errors?.discountPercentage) {
      missingItems.push("Discount %");
    }

    if (errors?.discountPrice) {
      missingItems.push("New price");
    }

    if (errors?.discountStartDate) {
      missingItems.push("Discount start date");
    }

    if (errors?.discountEndDate) {
      missingItems.push("Discount end date");
    }

    if (errors?.images) {
      missingItems.push("Variant images");
    }

    if (missingItems.length === 0) {
      return "Complete the required fields before saving";
    }

    return `Complete the following fields: ${missingItems.join(", ")}`;
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  useEffect(() => {
    if (!show) {
      hasInitializedOpenState.current = false;
      hasShownMissingProductNameRef.current = false;
      setVariantAttributes([]);
      setSelectedTemplateMeta(null);
      setSelectedTemplateId("");
      setShowVariantImagesModal(false);
      setActiveEditIndex(editIndex ?? null);
      setSubmitMode("close");
      submitModeRef.current = "close";
      return;
    }

    if (hasInitializedOpenState.current) {
      return;
    }

    hasInitializedOpenState.current = true;
    setActiveEditIndex(editIndex ?? null);

    const isEditModeWithAttributes = attributes.length > 0;
    setOpenedWithExistingAttributes(isEditModeWithAttributes);

    if (isEditModeWithAttributes) {
      setModalView("variants");
      setVariantAttributes(attributes);
      const existingTemplateMeta = getTemplateMetaFromAttributes(attributes);
      setSelectedTemplateMeta(
        existingTemplateMeta
          ? {
              id: existingTemplateMeta.id || "existing-template",
              name: existingTemplateMeta.name || "Selected attribute template",
              attributes: [],
            }
          : null,
      );
      return;
    }

    setModalView("templates");
    setSelectedTemplateId("");
    setSelectedTemplateMeta(null);
    setVariantAttributes([]);

    if (getAttributeTemplatesAction) {
      getAttributeTemplatesAction("CLEAR");
      getAttributeTemplatesAction({});
    }
  }, [show, attributes, editIndex, getAttributeTemplatesAction]);

  useEffect(() => {
    if (!show) {
      hasShownMissingProductNameRef.current = false;
      return;
    }

    if (normalizeText(productName || "")) {
      hasShownMissingProductNameRef.current = false;
      return;
    }

    if (hasShownMissingProductNameRef.current) {
      return;
    }

    hasShownMissingProductNameRef.current = true;
    showToast({
      title: "Fill a product name",
      description: "Add a product name before managing variants.",
      type: "error",
    });
    onClose();
  }, [onClose, productName, show, showToast]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [show, onClose]);

  const templateState = pageProps?.getAttributeTemplatesRequest;
  const templateRequestStatus = templateState?.request_status;
  const templateRequestData = templateState?.request_data?.response;
  const templateDetailState = pageProps?.getAttributeTemplateDetailRequest;
  const templateDetailStatus = templateDetailState?.request_status;
  const templateDetailError = templateDetailState?.request_data?.error;
  const categoryOptions = Array.isArray(
    pageProps?.getProductMetaDataRequest?.request_data?.response?.categories,
  )
    ? pageProps.getProductMetaDataRequest.request_data.response.categories.map((category: any) => ({
        label: category?.name,
        value: category?.name,
      }))
    : [];

  const templates: ProductAttributeTemplate[] = useMemo(() => {
    const rawTemplates = Array.isArray(templateRequestData) ? templateRequestData : [];

    return sortBySortOrder(
      rawTemplates
        .filter(isActiveItem)
        .map((template: ProductAttributeTemplate) => ({
          ...template,
          attributes: sortBySortOrder((template.attributes ?? []).filter(isActiveItem)),
        })),
    );
  }, [templateRequestData]);

  const filteredTemplates = useMemo(() => {
    const searchTerm = normalizeText(templateSearchTerm).toLowerCase();

    if (!searchTerm) {
      return templates;
    }

    return templates.filter((template) => {
      const searchableText = [
        template.name,
        template.description,
        ...(template.attributes ?? []).map((attribute) => attribute.name),
      ]
        .filter(Boolean)
        .join(" ");

      return normalizeText(searchableText).toLowerCase().includes(searchTerm);
    });
  }, [templateSearchTerm, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  const currentTemplateName =
    selectedTemplateMeta?.name ||
    selectedTemplate?.name ||
    variantAttributes[0]?.templateName ||
    "Selected attribute template";

  const templateBadgeLabels = useMemo(() => {
    if (selectedTemplateMeta?.attributes?.length) {
      return Array.from(new Set(getTemplateAttributes(selectedTemplateMeta)));
    }

    if (variantAttributes.length > 0) {
      return Array.from(
        new Set(variantAttributes.map((attribute) => attribute.name).filter(Boolean)),
      );
    }

    if (selectedTemplate) {
      return Array.from(new Set(getTemplateAttributes(selectedTemplate)));
    }

    return [];
  }, [selectedTemplateMeta, variantAttributes, selectedTemplate]);

  const renderedVariantCards = useMemo(
    () =>
      variants.map((variant, index) => {
        const displayName = getStoredVariantDisplayName(
          variant,
          index,
          variantAttributes,
          productName,
        );
        const summaryEntries = getUniqueBadgeEntries(
          getVariantSummaryEntries(variant, variantAttributes),
        );
        const keyBase =
          (isPersistedEntityId(variant.id) && variant.id?.trim()) ||
          `${displayName}-${buildVariantCombinationKey(variant.attributeValues ?? [])}-${index}`;

        return {
          key: keyBase,
          index,
          displayName,
          priceAmount: variant.price?.amount,
          stockQuantity: variant.stockQuantity,
          summaryEntries,
        };
      }),
    [productName, variantAttributes, variants],
  );

  const variantFormInitialValues = useMemo(
    () => {
      const activeVariant =
        activeEditIndex !== null
          ? variants[activeEditIndex] ?? initialEditVariant ?? null
          : null;

      return buildVariantFormInitialValues(activeVariant, variantAttributes);
    },
    [activeEditIndex, initialEditVariant, variantAttributes, variants],
  );

  useEffect(() => {
    if (!show || !formikRef.current) {
      return;
    }

    formikRef.current.resetForm({
      values: variantFormInitialValues,
    });
    formikRef.current.setStatus?.(undefined);
  }, [show, variantFormInitialValues]);

  const handleTemplateSelect = (template: ProductAttributeTemplate) => {
    setSelectedTemplateId(template.id);
    setSelectedTemplateMeta(template);
  };

  const renderHighlightedText = (text: string, searchTerm: string) => {
    const sourceText = String(text ?? "");
    const query = normalizeText(searchTerm);

    if (!query) {
      return sourceText;
    }

    const regex = new RegExp(escapeRegExp(query), "ig");
    const textSegments = sourceText.split(regex);
    const matchedSegments = sourceText.match(regex) ?? [];

    if (textSegments.length === 1) {
      return sourceText;
    }

    const nodes: React.ReactNode[] = [];

    textSegments.forEach((segment, index) => {
      if (segment) {
        nodes.push(<span key={`segment-${index}`}>{segment}</span>);
      }

      const match = matchedSegments[index];

      if (match) {
        nodes.push(
          <span className="template-search-highlight" key={`match-${index}`}>
            {match}
          </span>,
        );
      }
    });

    return nodes;
  };

  const handleContinueToVariants = async () => {
    if (!selectedTemplate || !getAttributeTemplateDetailAction) {
      return;
    }

    setSelectedTemplateMeta(selectedTemplate);
    setModalView("variants");
    getAttributeTemplateDetailAction("CLEAR");

    const detailResponse = await getAttributeTemplateDetailAction(selectedTemplate.id);

    if (!detailResponse?.attributes) {
      return;
    }

    const detailedAttributes = toEditableAttributes(detailResponse.attributes, {
      id: detailResponse.id,
      name: detailResponse.name,
    });

    setVariantAttributes(detailedAttributes);
    setAttributes(detailedAttributes);
    setSelectedTemplateMeta(detailResponse);
    setActiveEditIndex(null);
  };

  const handleBackToTemplates = () => {
    setModalView("templates");
  };

  const handleEditExistingVariant = (index: number) => {
    const variantName = getStoredVariantDisplayName(
      variants[index],
      index,
      variantAttributes,
      productName,
    );

    showToast({
      title: `${variantName} selected for editing`,
      type: "success",
    });
    setSubmitMode("close");
    submitModeRef.current = "close";
    setActiveEditIndex(index);
  };

  const handleDeleteExistingVariant = (index: number) => {
    const deletedVariantName = getStoredVariantDisplayName(
      variants[index],
      index,
      variantAttributes,
      productName,
    );
    const nextVariants = variants.filter((_, variantIndex) => variantIndex !== index);
    setVariants(nextVariants);
    showToast({
      title: `${deletedVariantName} has been deleted successfully`,
      type: "success",
    });

    if (activeEditIndex === index) {
      setActiveEditIndex(null);
      return;
    }

    if (activeEditIndex !== null && activeEditIndex > index) {
      setActiveEditIndex(activeEditIndex - 1);
    }
  };

  const renderEmptyTemplates = () => (
    <div className="variant-empty-state centered">
      <div className="variant-empty-icon" />
      <div className="variant-empty-title">No templates available</div>
      <div className="variant-empty-copy">
        Attribute templates will show up here once active templates are available.
      </div>
    </div>
  );

  const renderNoSearchResults = () => (
    <div className="variant-empty-state centered">
      <div className="variant-empty-icon" />
      <div className="variant-empty-title">No templates match your search</div>
      <div className="variant-empty-copy">
        Try another keyword or clear the search to view every active template.
      </div>
    </div>
  );

  const renderTemplatePicker = () => (
    <div className="variance-section">
      {templateRequestStatus === "GET_ATTRIBUTE_TEMPLATES_PENDING" && <LoadingItem />}

      {templateRequestStatus !== "GET_ATTRIBUTE_TEMPLATES_PENDING" &&
        templates.length === 0 &&
        renderEmptyTemplates()}

      {templates.length > 0 && (
        <>
          <div className="template-grid-heading">Select attribute template</div>
          <div className="template-grid-note">
            You can only select one attribute template for a product.
          </div>
          <div className="template-search-bar">
            <input
              className="template-search-input"
              type="search"
              value={templateSearchTerm}
              onChange={(event) => setTemplateSearchTerm(event.target.value)}
              placeholder="Search templates"
              aria-label="Search attribute templates"
            />
          </div>
          {filteredTemplates.length === 0 ? (
            renderNoSearchResults()
          ) : (
            <div className="template-grid">
              {filteredTemplates.map((template) => {
              const templateAttributes = getTemplateAttributes(template);
              const isSelected = selectedTemplateId === template.id;

              return (
                <button
                  key={template.id}
                  type="button"
                  className={isSelected ? "template-card active" : "template-card"}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className="template-card-title">
                    {renderHighlightedText(template.name, templateSearchTerm)}
                  </div>
                  <div className="template-card-description">
                    {renderHighlightedText(
                      template.description || "No description available",
                      templateSearchTerm,
                    )}
                  </div>
                  <div className="template-card-attributes">
                    {templateAttributes.length > 0 ? (
                      templateAttributes.map((attributeName) => (
                        <span className="template-attribute-pill" key={`${template.id}-${attributeName}`}>
                          {renderHighlightedText(attributeName, templateSearchTerm)}
                        </span>
                      ))
                    ) : (
                      <span className="template-attribute-pill muted">No attributes</span>
                    )}
                  </div>
                </button>
              );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderVariantCards = () => {
    if (renderedVariantCards.length === 0) {
      return null;
    }

    return (
      <div className="modal-variant-list">
        {renderedVariantCards.map((variantCard) => (
          <div
            className={
              activeEditIndex === variantCard.index
                ? "modal-variant-card active"
                : "modal-variant-card"
            }
            key={variantCard.key}
          >
            <div className="modal-variant-card-top">
              <div className="modal-variant-card-title">{variantCard.displayName}</div>
              <div className="modal-variant-card-actions">
                <button
                  type="button"
                  className="variant-icon-btn"
                  onClick={() => handleEditExistingVariant(variantCard.index)}
                  aria-label="Edit variant"
                >
                  <img src={EditIcon} alt="" />
                </button>
                <button
                  type="button"
                  className="variant-icon-btn delete"
                  onClick={() => handleDeleteExistingVariant(variantCard.index)}
                  aria-label="Delete variant"
                >
                  <img src={TrashIcon} alt="" />
                </button>
              </div>
            </div>
            <div className="modal-variant-card-chips">
              {variantCard.summaryEntries.map((entry) => (
                <span className="modal-variant-chip" key={`${entry.key}-${entry.value}`}>
                  {entry.key}: {entry.value}
                </span>
              ))}
              <span className="modal-variant-chip">Price: ${variantCard.priceAmount}</span>
              <span className="modal-variant-chip">Stock Qty: {variantCard.stockQuantity}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderVariantView = () => {
    if (templateDetailStatus === "GET_ATTRIBUTE_TEMPLATE_DETAIL_PENDING" && variantAttributes.length === 0) {
      return (
        <div className="variance-section">
          <LoadingItem />
        </div>
      );
    }

    if (templateDetailStatus === "GET_ATTRIBUTE_TEMPLATE_DETAIL_FAILURE" && variantAttributes.length === 0) {
      return (
        <div className="variance-section">
          <AlertMsg type="error" message={templateDetailError || "Unable to load template options"} />
        </div>
      );
    }

    return (
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={variantFormInitialValues}
        validate={(values) => {
          const errors: any = {};
          const attributeErrors: Record<string, string> = {};
          const priceAmount = parseNumericValue(values.price);
          const stockQuantity = parseNumericValue(values.stockQuantity);

          variantAttributes.forEach((attribute) => {
            if (attribute.isRequired && !values.attributeSelections[attribute.code]) {
              attributeErrors[attribute.code] = "Required";
            }
          });

          if (Object.keys(attributeErrors).length > 0) {
            errors.attributeSelections = attributeErrors;
          }

          if (priceAmount === null || priceAmount <= 0) {
            errors.price = "Enter a valid price";
          }

          if (stockQuantity === null || stockQuantity < 0) {
            errors.stockQuantity = "Enter a valid stock quantity";
          }

          if (values.images.length > 3) {
            errors.images = "Only 3 images are allowed";
          }

          if (values.addDiscount) {
            const discountPercent = parseNumericValue(values.discountPercentage);
            const discountAmount = parseNumericValue(values.discountPrice);

            if (discountPercent === null || discountPercent <= 0 || discountPercent > 100) {
              errors.discountPercentage = "Enter a valid discount percentage";
            }

            if (discountAmount === null || discountAmount <= 0) {
              errors.discountPrice = "New price is required";
            }

            if (!values.discountStartDate) {
              errors.discountStartDate = "Start date is required";
            }

            if (!values.discountEndDate) {
              errors.discountEndDate = "End date is required";
            }
          }

          return errors;
        }}
        onSubmit={(values, helpers) => {
          const activeSubmitMode = submitModeRef.current;
          const priceAmount = parseNumericValue(values.price);
          const stockQuantity = parseNumericValue(values.stockQuantity);
          const discountAmount = parseNumericValue(values.discountPrice);

          if (priceAmount === null || stockQuantity === null) {
            helpers.setStatus("Complete the required fields before saving");
            showToast({
              title: "Incomplete variant details",
              description: "Complete the required fields before saving.",
              type: "error",
            });
            return;
          }

          const attributeValues = variantAttributes
            .map((attribute, index) => {
              const selectedValue = values.attributeSelections[attribute.code];

              if (!selectedValue) {
                return null;
              }

              return {
                attributeName: attribute.name,
                value: selectedValue,
                sortOrder: attribute.sortOrder ?? index + 1,
              };
            })
            .filter(Boolean) as Variant["attributeValues"];

          const nextCombinationLabel = buildVariantNameFromSelections(
            values.attributeSelections,
            variantAttributes,
          );
          const resolvedVariantName = toVariantNameText(
            buildVariantNameFromProductSelections(
              productName,
              values.attributeSelections,
              variantAttributes,
              activeEditIndex !== null ? variants[activeEditIndex]?.name : values.variantName,
              activeEditIndex !== null ? activeEditIndex : variants.length,
            ) || nextCombinationLabel,
          );

          const nextVariant: Variant = {
            ...(activeEditIndex !== null && isPersistedEntityId(variants[activeEditIndex]?.id)
              ? { id: variants[activeEditIndex].id?.trim() }
              : {}),
            name: resolvedVariantName,
            stockQuantity,
            availableQuantity: stockQuantity,
            isActive: true,
            sortOrder:
              activeEditIndex !== null
                ? variants[activeEditIndex]?.sortOrder ?? activeEditIndex + 1
                : variants.length + 1,
            urls: values.images.map((image) => image.url).filter(Boolean),
            attributeValues,
            price: {
              amount: priceAmount,
            },
            addDiscount: values.addDiscount,
            discountPercentage: values.addDiscount
              ? parseNumericValue(values.discountPercentage) ?? undefined
              : undefined,
            discountPrice:
              values.addDiscount && discountAmount !== null
                ? {
                    amount: discountAmount,
                    startDate: formatDateForPayload(values.discountStartDate),
                    dueDate: formatDateForPayload(values.discountEndDate),
                  }
                : undefined,
            images: values.images,
          };

          const nextVariants = [...variants];

          if (activeEditIndex !== null) {
            nextVariants[activeEditIndex] = nextVariant;
          } else {
            nextVariants.push(nextVariant);
          }

          if (hasDuplicateVariantCombination(nextVariants)) {
            helpers.setStatus("This variant combination already exists");
            showToast({
              title: "Duplicate variant",
              description: `A variant with ${nextCombinationLabel || "the same combination"} already exists.`,
              type: "error",
            });
            return;
          }

          setVariants(nextVariants);
          helpers.setStatus(undefined);

          if (activeSubmitMode === "continue") {
            showToast({
              title: `${nextVariant.name} has been saved successfully`,
              type: "success",
            });
            setActiveEditIndex(null);
            setSubmitMode("close");
            submitModeRef.current = "close";
            helpers.resetForm({
              values: buildVariantFormInitialValues(null, variantAttributes),
            });
            return;
          }

          if (activeEditIndex !== null) {
            showToast({
              title: `${nextVariant.name} has been saved successfully`,
              type: "success",
            });
            setActiveEditIndex(null);
            setSubmitMode("close");
            submitModeRef.current = "close";
            helpers.resetForm({
              values: buildVariantFormInitialValues(null, variantAttributes),
            });
            return;
          }

          setSubmitMode("close");
          submitModeRef.current = "close";
          onClose();
        }}
      >
        {({
          errors,
          handleSubmit,
          setFieldTouched,
          setFieldValue,
          status,
          submitForm,
          touched,
          values,
        }) => {
          const hasOddAttributeCount = variantAttributes.length % 2 === 1;
          const derivedVariantName = buildVariantNameFromProductSelections(
            productName,
            values.attributeSelections,
            variantAttributes,
            activeEditIndex !== null ? variants[activeEditIndex]?.name : values.variantName,
            activeEditIndex !== null ? activeEditIndex : variants.length,
          );

          return (
            <>
              {showVariantImagesModal && (
                <ImageDrawer
                  pageProps={pageProps}
                  showDrawer={showVariantImagesModal}
                  catListItems={categoryOptions}
                  onClose={() => setShowVariantImagesModal(false)}
                  selectedImages={values.images}
                  maxImages={3}
                  applyButtonLabel="Add to Variant"
                  onApply={(images: any[]) => {
                    const limitedImages = images
                      .filter((image) => image?.url)
                      .slice(0, 3)
                      .map((image, index) => ({
                        index,
                        url: image.url,
                        otherNames: image.otherNames ?? image.othernames ?? [],
                      }));

                    setFieldValue("images", limitedImages);
                    setFieldTouched("images", true, true);
                    setShowVariantImagesModal(false);
                  }}
                />
              )}

              <div className="variance-section variance-form-layout">
                {!openedWithExistingAttributes ? (
                  <div className="variance-top-actions">
                    <button
                      className="variance-btn secondary variance-back-button"
                      type="button"
                      onClick={handleBackToTemplates}
                    >
                      Back
                    </button>
                  </div>
                ) : null}

                <div className="selected-template-block">
                  <div className="selected-template-name">{currentTemplateName}</div>
                  <div className="selected-template-badges">
                    {templateBadgeLabels.map((label) => (
                      <span className="template-attribute-pill" key={label}>
                        {label}
                      </span>
                    ))}
                  </div>
                </div>

                {renderVariantCards()}

                {status ? <div className="variant-form-error global">{status}</div> : null}

                <Form className="variant-form" onSubmit={handleSubmit}>
                  <div className="variant-form-grid">
                    <div className="variant-field full-span">
                      <label htmlFor="variantName">Variant name</label>
                      <input
                        id="variantName"
                        type="text"
                        autoComplete="off"
                        value={derivedVariantName}
                        readOnly
                        disabled
                        className="input-wrap inapp-input variant-input variant-input-derived"
                      />
                    </div>

                    {variantAttributes.map((attribute, index) => {
                      const isLastOddAttribute =
                        hasOddAttributeCount && index === variantAttributes.length - 1;

                      return (
                        <div
                          className={isLastOddAttribute ? "variant-field full-span" : "variant-field"}
                          key={attribute.code}
                        >
                          <label htmlFor={`attributeSelections.${attribute.code}`}>
                            {attribute.name}
                            {attribute.isRequired ? <span className="required-indicator">*</span> : null}
                          </label>
                          <div className="select-field-wrap">
                            <Field
                              as="select"
                              id={`attributeSelections.${attribute.code}`}
                              name={`attributeSelections.${attribute.code}`}
                              value={values.attributeSelections?.[attribute.code] || ""}
                              className={`input-wrap inapp-input variant-input ${
                                errors.attributeSelections?.[attribute.code] &&
                                touched.attributeSelections?.[attribute.code]
                                  ? "is-invalid-input"
                                  : ""
                              }`}
                            >
                              <option value="">Select {attribute.name.toLowerCase()}</option>
                              {attribute.options.map((option) => (
                                <option key={`${attribute.code}-${option.value}`} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage
                            name={`attributeSelections.${attribute.code}`}
                            className="form-input-error"
                            component="div"
                          />
                        </div>
                      );
                    })}

                    <div className="variant-field">
                      <label htmlFor="price">Price</label>
                      <div className="price-wrap variant-price-wrap">
                        <span className="currency-pre">$</span>
                        <Field
                          id="price"
                          name="price"
                          type="text"
                          autoComplete="off"
                          value={numberWithCommas(values.price)}
                          onChange={(event: any) => {
                            const valueSet = numberWithCommas(event.target.value);
                            setFieldTouched("price", true, true);
                            setFieldValue("price", valueSet);

                            if (!values.addDiscount) {
                              return;
                            }

                            const priceValue = parseNumericValue(String(valueSet ?? ""));
                            const discountPercent = parseNumericValue(
                              String(values.discountPercentage ?? ""),
                            );

                            if (priceValue !== null && discountPercent !== null) {
                              const nextPrice =
                                priceValue - (priceValue * discountPercent) / 100;
                              setFieldValue(
                                "discountPrice",
                                nextPrice > 0 ? nextPrice.toFixed(2) : "",
                              );
                            } else {
                              setFieldValue("discountPrice", "");
                            }
                          }}
                          className={`input-wrap inapp-input variant-input ${
                            errors.price && touched.price ? "is-invalid-input" : ""
                          }`}
                        />
                      </div>
                      <ErrorMessage name="price" className="form-input-error" component="div" />
                    </div>

                    <div className="variant-field">
                      <label htmlFor="stockQuantity">Stock Quantity</label>
                      <Field
                        id="stockQuantity"
                        name="stockQuantity"
                        type="text"
                        autoComplete="off"
                        value={allowNumbersOnly(values.stockQuantity)}
                        onChange={(event: any) => {
                          setFieldTouched("stockQuantity", true, true);
                          setFieldValue("stockQuantity", allowNumbersOnly(event.target.value));
                        }}
                        className={`input-wrap inapp-input variant-input ${
                          errors.stockQuantity && touched.stockQuantity ? "is-invalid-input" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="stockQuantity"
                        className="form-input-error"
                        component="div"
                      />
                    </div>

                    <div className="variant-field">
                      <label htmlFor="images">Upload Image (Optional)</label>
                      <button
                        type="button"
                        className="variant-upload-box"
                        onClick={() => setShowVariantImagesModal(true)}
                      >
                        <span className="variant-upload-icon" aria-hidden="true" />
                        <span>Upload images (3 Max)</span>
                      </button>
                      <ErrorMessage name="images" className="form-input-error" component="div" />
                      <div className="variant-image-grid">
                        {[0, 1, 2].map((index) => {
                          const image = values.images[index];

                          return (
                            <div
                              className={image ? "variant-image-card filled" : "variant-image-card"}
                              key={index}
                            >
                              {image ? (
                                <>
                                  <img src={image.url} alt={`Variant ${index + 1}`} />
                                  <button
                                    type="button"
                                    className="variant-image-remove"
                                    onClick={() => {
                                      const nextImages = values.images.filter(
                                        (_, imageIndex) => imageIndex !== index,
                                      );
                                      setFieldValue("images", nextImages);
                                    }}
                                  >
                                    &times;
                                  </button>
                                </>
                              ) : (
                                <span>Image {index + 1}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="variant-field discount-panel">
                      <label className="discount-toggle" htmlFor="variantAddDiscount">
                        <Field id="variantAddDiscount" type="checkbox" name="addDiscount" />
                        <span>Add a discount</span>
                      </label>

                      {values.addDiscount ? (
                        <div className="variant-form-grid discount-grid">
                          <div className="variant-field">
                            <label htmlFor="variantDiscountPercentage">Discount %</label>
                            <Field
                              id="variantDiscountPercentage"
                              name="discountPercentage"
                              type="text"
                              autoComplete="off"
                              value={allowNumbersOnly(values.discountPercentage, 3)}
                              onChange={(event: any) => {
                                const nextPercent = allowNumbersOnly(event.target.value, 3);
                                setFieldTouched("discountPercentage", true, true);
                                setFieldValue("discountPercentage", nextPercent);

                                const priceValue = parseNumericValue(values.price);
                                const discountPercent = parseNumericValue(
                                  String(nextPercent ?? ""),
                                );

                                if (
                                  priceValue !== null &&
                                  discountPercent !== null &&
                                  discountPercent <= 100
                                ) {
                                  const nextPrice =
                                    priceValue - (priceValue * discountPercent) / 100;
                                  setFieldValue(
                                    "discountPrice",
                                    nextPrice > 0 ? nextPrice.toFixed(2) : "",
                                  );
                                } else {
                                  setFieldValue("discountPrice", "");
                                }
                              }}
                              className={`input-wrap inapp-input variant-input ${
                                errors.discountPercentage && touched.discountPercentage
                                  ? "is-invalid-input"
                                  : ""
                              }`}
                              placeholder="Discount %"
                            />
                            <ErrorMessage
                              name="discountPercentage"
                              className="form-input-error"
                              component="div"
                            />
                          </div>

                          <div className="variant-field">
                            <label htmlFor="variantDiscountPrice">New price</label>
                            <div className="price-wrap variant-price-wrap">
                              <span className="currency-pre">$</span>
                              <Field
                                id="variantDiscountPrice"
                                name="discountPrice"
                                type="text"
                                autoComplete="off"
                                value={numberWithCommas(values.discountPrice)}
                                onChange={(event: any) => {
                                  const valueSet = numberWithCommas(event.target.value);
                                  setFieldTouched("discountPrice", true, true);
                                  setFieldValue("discountPrice", valueSet);

                                  const priceValue = parseNumericValue(values.price);
                                  const newPrice = parseNumericValue(String(valueSet ?? ""));

                                  if (
                                    priceValue !== null &&
                                    newPrice !== null &&
                                    newPrice < priceValue &&
                                    priceValue > 0
                                  ) {
                                    const percentage = Math.round(
                                      ((priceValue - newPrice) / priceValue) * 100,
                                    );
                                    setFieldValue("discountPercentage", String(percentage));
                                  }
                                }}
                                className={`input-wrap inapp-input variant-input ${
                                  errors.discountPrice && touched.discountPrice
                                    ? "is-invalid-input"
                                    : ""
                                }`}
                              />
                            </div>
                            <ErrorMessage
                              name="discountPrice"
                              className="form-input-error"
                              component="div"
                            />
                          </div>

                          <div className="variant-field">
                            <label htmlFor="variantDiscountStartDate">Start date</label>
                            <DatePicker
                              id="variantDiscountStartDate"
                              selected={values.discountStartDate}
                              onChange={(date: Date | null) => {
                                setFieldTouched("discountStartDate", true, true);
                                setFieldValue("discountStartDate", date);
                              }}
                              className={`input-wrap inapp-input variant-input ${
                                errors.discountStartDate && touched.discountStartDate
                                  ? "is-invalid-input"
                                  : ""
                              }`}
                              placeholderText="Select start date"
                              dateFormat="dd/MM/yyyy"
                            />
                            <ErrorMessage
                              name="discountStartDate"
                              className="form-input-error"
                              component="div"
                            />
                          </div>

                          <div className="variant-field">
                            <label htmlFor="variantDiscountEndDate">End date</label>
                            <DatePicker
                              id="variantDiscountEndDate"
                              selected={values.discountEndDate}
                              onChange={(date: Date | null) => {
                                setFieldTouched("discountEndDate", true, true);
                                setFieldValue("discountEndDate", date);
                              }}
                              className={`input-wrap inapp-input variant-input ${
                                errors.discountEndDate && touched.discountEndDate
                                  ? "is-invalid-input"
                                  : ""
                              }`}
                              placeholderText="Select end date"
                              dateFormat="dd/MM/yyyy"
                              minDate={values.discountStartDate || undefined}
                            />
                            <ErrorMessage
                              name="discountEndDate"
                              className="form-input-error"
                              component="div"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="variant-inline-actions">
                    <div className="variant-inline-actions-left">
                      <span />
                    </div>
                    <div className="variant-inline-actions-right">
                      <button
                        className="variance-btn tertiary"
                        type="button"
                        onClick={() => {
                          setSubmitMode("continue");
                          submitModeRef.current = "continue";
                          submitForm();
                        }}
                      >
                        + Add Another Variant
                      </button>
                    </div>
                  </div>
                </Form>
              </div>

            </>
          );
        }}
      </Formik>
    );
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <div className="custom-modal-backdrop" onClick={onClose} />
      <div className="custom-modal variant-modal">
        <div className="custom-modal-dialog variance-shell">
          <div className="custom-modal-header variance-shell-header">
            <h2 className="custom-modal-title variance-header">
              {modalView === "templates" ? "Configure Product Variants" : "Add a Product Variance"}
            </h2>
            <button
              className="custom-modal-close variance-close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              &times;
            </button>
          </div>

          <div className="custom-modal-body variance-shell-body">
            {modalView === "templates" ? renderTemplatePicker() : renderVariantView()}
          </div>

          {modalView === "templates" ? (
            <div className="product-cta variance-footer">
              <button className="variance-btn secondary" type="button" onClick={onClose}>
                Cancel
              </button>
              <button
                className="variance-btn primary"
                type="button"
                disabled={!selectedTemplate}
                onClick={handleContinueToVariants}
              >
                Next
              </button>
            </div>
          ) : (
            <div className="product-cta variance-footer variant-footer">
              <button
                className="variance-btn primary full-width"
                type="button"
                onClick={async () => {
                  setSubmitMode("close");
                  submitModeRef.current = "close";

                  const validationErrors = await formikRef.current?.validateForm?.();

                  if (Object.keys(validationErrors || {}).length > 0) {
                    const attributeTouched = variantAttributes.reduce<Record<string, boolean>>(
                      (accumulator, attribute) => {
                        accumulator[attribute.code] = true;
                        return accumulator;
                      },
                      {},
                    );

                    formikRef.current?.setTouched?.({
                      attributeSelections: attributeTouched,
                      price: true,
                      stockQuantity: true,
                      addDiscount: true,
                      discountPercentage: true,
                      discountPrice: true,
                      discountStartDate: true,
                      discountEndDate: true,
                      images: true,
                    });

                    showToast({
                      title: "Missing required fields",
                      description: getValidationToastMessage(validationErrors),
                      type: "error",
                    });
                    return;
                  }

                  formikRef.current?.submitForm?.();
                }}
              >
                {activeEditIndex !== null ? "Save Changes" : "Add Variance"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductVarianceModal;
